import { BrightSpan } from '@components/BrightSpan/BrightSpan';
import { LoadingActionButton } from '@components/LoadingActionButton/LoadingActionButton';
import { PriceDisplay } from '@components/PriceDisplay/PriceDisplay';
import { formatCreditsWithUnit, formatNumber } from '@lib/utils/units';
import { Product } from '@stores/Billing';
import classNames from 'classnames';
import React from 'react';
import { ActionButtonProps } from '../../../ActionButton';
import css from './TierEntry.module.scss';

interface CtaProps {
  text: string;
  variant: ActionButtonProps['variant'];
  onClick?: () => void | Promise<void>;
}

type TierEntryProps = {
  caption?: string;
  ctaProps?: CtaProps;
  children: React.ReactNode;
  highlighted?: boolean;
  /**
   * Label shown in the badge strip above the card (e.g. "Best value"). When set,
   * the badge + its purple gradient are revealed; when unset, an equal-height
   * placeholder is reserved so every card stays vertically aligned.
   */
  badge?: React.ReactNode;
  tier?: string;
  specialComponent?: React.ReactNode;
  shadowOpacity?: number;
  tierEntryLeft?: boolean;
  tierEntryRight?: boolean;
  brightSpan?: string;
  smallPrice?: boolean;
  disabledProduct?: boolean;
  sectionId?: string;
} & (
  | {
      product: Product;
      productType: 'subscription' | 'one-time';
    }
  | {
      product: { name: string; tagline: string; price: string };
      productType: 'custom';
    }
);

export const TierEntry: React.FC<TierEntryProps> = ({
  product,
  caption,
  ctaProps,
  children,
  highlighted = false,
  badge,
  tier,
  specialComponent,
  shadowOpacity = 0,
  tierEntryLeft = false,
  tierEntryRight = false,
  brightSpan,
  smallPrice = false,
  productType,
  disabledProduct = false,
  sectionId,
}) => {
  return (
    <div className={classNames(css.tierEntryContainer, !!badge && css.tierEntryContainerActive)}>
      <span className={classNames(css.topBadge, !!badge && css.topBadgeActive)}>
        {badge ?? ' '}
      </span>
      <div
        id={sectionId}
        className={classNames(
          css.tierEntry,
          tierEntryLeft && css.tierEntryLeft,
          tierEntryRight && css.tierEntryRight,
          disabledProduct && css.disabledProduct,
        )}
        data-highlighted={highlighted}
        data-tier={tier}
        style={
          highlighted ? ({ '--shadow-opacity': shadowOpacity } as React.CSSProperties) : undefined
        }
      >
        <h3 className={css.title}>
          {product.name}
          {brightSpan && <BrightSpan>{brightSpan}</BrightSpan>}
        </h3>
        <p className={css.description}>{product.tagline}</p>
        <div className={classNames(css.price, smallPrice && css.smallPrice)}>
          {specialComponent && <div className={css.specialComponent}>{specialComponent}</div>}
          {typeof product.price === 'string' ? (
            <span>{product.price}</span>
          ) : (
            <PriceDisplay
              price={product.price}
              textAfterPrice={
                productType === 'subscription'
                  ? `per ${product.price.interval}`
                  : productType === 'one-time'
                    ? product.name.toLowerCase().includes('credits')
                      ? `for ${formatCreditsWithUnit(product.quota.credits, undefined, 'extra')}`
                      : product.name.toLowerCase().includes('storage')
                        ? // Currently dead code because we don't have storage hours among our products.
                          `for ${formatNumber(product.quota.storageHours)} extra storage hours`
                        : ''
                    : ''
              }
            />
          )}
          {caption && <span className={css.caption}>{caption}</span>}
        </div>
        <hr className={css.divider} />
        {children}
        {ctaProps && (
          <div className={classNames(css.cta)}>
            <LoadingActionButton
              variant={ctaProps.variant}
              fullWidth
              onClick={ctaProps.onClick || (() => {})}
              loadingText="Processing..."
            >
              {ctaProps.text}
            </LoadingActionButton>
          </div>
        )}
      </div>
    </div>
  );
};
